import { ChangeEvent } from "react";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

interface ITextAreaAutoSizeProps {
    id?: string;
    dataCy?: string;
    disabled?: boolean;
    cssClass?: string;
    onTextChangeCallback?: (value: string) => void;
    onTextChangeOperation?: number;
    onBlurCallBack?: () => void;
    value?: string;
    focus?: boolean;
    nowrap?: boolean
    minHeight?: string;
    maxHeight?: string;
    isInvalid?: boolean;
    maxLength?: number;
}

class TextAreaAutoSizeState {
    public value: string;
}

class DefaultTextAreaAutoSizeState extends TextAreaAutoSizeState {
    constructor(props: ITextAreaAutoSizeProps) {
        super();
        this.value = props.value || "";
    }
}

class TextAreaAutoSize extends BaseReactComponent<ITextAreaAutoSizeProps, TextAreaAutoSizeState> {
    public id: string;
    private timeoutReference: number;

    constructor(props: ITextAreaAutoSizeProps) {
        super(props);

        this.id = this.props.id || GuidHelper.NewGuid();
        this.state = new DefaultTextAreaAutoSizeState(this.props);
    }

    public componentWillReceiveProps(nextProps: ITextAreaAutoSizeProps): void {
        if (nextProps.id != this.props.id) {
            this.mutateState(s => { s.value = nextProps.value; });
            this.id = nextProps.id;
        }
    }

    public render(): JSX.Element {
        return (
            <textarea
                ref={this.id}
                onChange={this.onChange.bind(this)}
                value={this.state.value}
                id={this.id}
                data-cy={this.props.dataCy}
                style={this.cssStyle}
                className={`vresize form-control ${this.props.cssClass ?? ""} ${this.props.isInvalid ? "invalid" : ""}`}
                disabled={this.props.disabled === true}
                onBlur={() => this.onBlur()}
                maxLength={this.props.maxLength}
            />
        );
    }

    public onBlur(): void {
        this.props.onBlurCallBack && this.props.onBlurCallBack();
    }

    public componentDidMount(): void {
        if (this.props.id) {
            let controlRef = this.refs[this.props.id] as HTMLTextAreaElement;
            autosize(controlRef);
        }

        if (this.props.focus === true)
            $("#" + this.id).focus();
    }

    public componentWillUnmount(): void {
        try {
            let evt = document.createEvent("Event");
            evt.initEvent("autosize:destroy", true, false);

            if (this.props.id) {
                let controlRef = this.refs[this.props.id] as HTMLTextAreaElement;
                controlRef.dispatchEvent(evt);
            }
        }
        catch (e) {
            console.warn("failed to destroy autoresize box");
        }
        super.componentWillUnmount();
    }

    private get cssStyle(): React.CSSProperties {
        let cssStyle: React.CSSProperties = {};

        if (this.props.nowrap === true)
            cssStyle.whiteSpace = "pre";

        if (this.props.minHeight)
            cssStyle.minHeight = this.props.minHeight;

        if (this.props.maxHeight)
            cssStyle.maxHeight = this.props.maxHeight;

        return cssStyle;
    }

    private onChange(e: ChangeEvent<HTMLTextAreaElement>): void {
        let value = e.target.value as string;

        this.mutateState(s => {
            s.value = value;
        });

        if (this.timeoutReference !== -1) {
            clearTimeout(this.timeoutReference);
            this.timeoutReference = -1;
        }

        let self = this;
        this.timeoutReference = window.setTimeout(() => {
            self.props.onTextChangeCallback && self.props.onTextChangeCallback(value);

            if (!IsNullOrUndefined(self.props.onTextChangeOperation)) {
                let operation = new LocalEvent(self.props.onTextChangeOperation);
                operation.AddItem("value", value);
                LocalEventDispatcher.dispatch(operation);
            }
        }, 200);
    }
}

export { TextAreaAutoSize };
